import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import DatePicker from "../components/inputFields/DatePicker";
import Button from "../components/buttons/Button";
import { createCrop, getCropsMasterData, getMasterDropdownData, userFarmingCommodityData } from "../services/cropService";
import FormikDropdown from "../components/inputFields/FormikDropDown";
import { toast } from "react-toastify";

const seasons = [
    { label: "Select Season", value: "", disabled: true },
    { label: "Kharif", value: "Kharif" },
    { label: "Rabi", value: "Rabi" },
    { label: "Zaid", value: "Zaid" }
];

// Validation schema using Yup
const validationSchema = Yup.object({
    commodity: Yup.string().required("Please select a commodity"),
    season: Yup.string().required("Please select a season"),
    showing_month: Yup.string().required("Please select a sowing date"),
    harvest_month: Yup.string()
        .required("Please select a harvesting date")
        .test("after-sowing", "Harvesting date must be after sowing date", function (value) {
            const { showing_month } = this.parent;
            if (!value || !showing_month) return true;
            return new Date(value) > new Date(showing_month);
        }),
});

function CommodityForm({ item, onSave }) {
    const [myCrops, setMyCrops] = useState([]);
    const [farms, setFarms] = useState([]);
    const [commodityVarieints, setCommodityVarients] = useState([]);
    const [selectedFarms, setSelectedFarms] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);

    // Initial form values
    const initialValues = {
        commodity: "",
        season: "",
        variant: "",
        sowing_date: "",
        harvest_date: "",
    };

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            setIsLoadingData(true);
            const cropsData = await getCropsMasterData(item.id);
            setMyCrops(cropsData.data || []);
        } catch (error) {
            console.error("Error loading initial data:", error);
        } finally {
            setIsLoadingData(false);
        }
    };

    const loadDropdownData = async (commodity) => {
        try {
            const cropsData = await getMasterDropdownData(item.id, commodity);
            setFarms(cropsData.data.user_farms);
            setCommodityVarients(cropsData.data.commodity_variants);
        } catch (error) {
            console.error("Error loading initial data:", error);
        }
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        setSubmitting(true);

        try {
            // Prepare data for API
            const cropData = {
                season: values.season,
                sowing_date: values.showing_month,
                harvest_date: values.harvest_month,
                user: item.id,
                commodity: values.commodity,
                variant: values.variant,
                farms: selectedFarms,
            };

            // Create crop
            const response = await userFarmingCommodityData(cropData);

            if (response.status === 200 || response.status === 201) {
                toast.success("Farming Commodity added successfully");
                resetForm();
                setSelectedFarms([]);
                onSave();
            } else {
                toast.error("Something went wrong");
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            toast.error("Failed to save crop");
        } finally {
            setSubmitting(false);
        }
    };

    if (isLoadingData) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
            >
                {({ values, isSubmitting, resetForm, setFieldValue, setFieldTouched }) => (
                    <Form className="space-y-6">
                        <FormikDropdown
                            label="Select Commodity"
                            name="commodity"
                            options={myCrops.filter((e) => !e.user_commodity).map((crop) => ({
                                value: crop.id,
                                label: crop.name,
                            }))}
                            onChange={(selected) => {
                                setFieldValue("commodity", selected.value);
                                loadDropdownData(selected.value);
                            }}
                            placeholder="Select Commodity"
                            required={true}
                            needSearch={true}
                        />
                        <FormikDropdown
                            label="Select Variant"
                            options={commodityVarieints.filter((e) => !e.user_commodity).map((crop) => ({
                                value: crop.name,
                                label: crop.name,
                            }))}
                            name="variant"
                            placeholder="Select Season"
                            required={true}
                        />
                        <FormikDropdown
                            label="Select Season"
                            options={seasons}
                            name="season"
                            placeholder="Select Season"
                            required={true}
                        />

                        {/* Date Selection Row */}
                        <div className="flex flex-row items-center gap-4">
                            <div className="flex-1 w-full">
                                <DatePicker
                                    label="Sowing Date"
                                    name="showing_month"
                                    value={values.showing_month}
                                    onChange={(date) => {
                                        setFieldValue("showing_month", date);
                                        setFieldTouched("showing_month", true);
                                    }}
                                    placeholder="Select Sowing Date"
                                    required={true}
                                    minDate={new Date(1999, 0, 1)}
                                    maxDate={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)}
                                />
                            </div>
                            <div className="flex-1 w-full">
                                <DatePicker
                                    label="Harvesting Date"
                                    name="harvest_month"
                                    value={values.harvest_month}
                                    onChange={(date) => {
                                        setFieldValue("harvest_month", date);
                                        setFieldTouched("harvest_month", true);
                                    }}
                                    placeholder="Select Harvesting Date"
                                    required={true}
                                    minDate={new Date(1999, 0, 1)}
                                    maxDate={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)}
                                />
                            </div>
                        </div>

                        {/* Farm Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Farms <span className="text-red-500">*</span>
                            </label>
                            {farms.length > 0 ? (
                                <div className="flex flex-wrap gap-3">
                                    {farms.map((farm) => (
                                        <button
                                            key={farm.id}
                                            type="button"
                                            onClick={() => {
                                                const currentFarms = selectedFarms;
                                                const newFarms = currentFarms.includes(farm.id)
                                                    ? currentFarms.filter((id) => id !== farm.id)
                                                    : [...currentFarms, farm.id];
                                                setSelectedFarms(newFarms);
                                            }}
                                            className={`px-4 py-2 rounded-md border-2 transition-colors duration-200 ${selectedFarms.includes(farm.id)
                                                ? "bg-primary text-white border-primary"
                                                : "bg-white text-primary border-primary hover:bg-primary hover:text-white"
                                                }`}
                                        >
                                            {farm.name}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-gray-500 text-sm">No farms available</div>
                            )}
                        </div>
                        {/* Submit Button */}
                        <div className="flex justify-end space-x-4">
                            <Button
                                type="button"
                                onClick={() => { resetForm(); setFarms([]); }}
                                className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md"
                            >
                                Reset
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-md disabled:opacity-50"
                            >
                                {isSubmitting ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}

export default CommodityForm;
